import ClearIcon from "@mui/icons-material/Clear";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Divider, IconButton, InputAdornment, Skeleton, TextField, Typography } from "@mui/material";
import useInfiniteCursorConnectionScroll from "apps/web/src/components/universal/InfiniteScroll/useInfiniteCursorConnectionScroll";
import InfiniteScrollContainer from "apps/web/src/components/universal/InfiniteScroll/InfiniteScrollContainer";
import { MenuItemPreviewSearchFragment, useMenuItemSearchQuery } from "apps/web/src/types";
import { useDebounce } from "shared-ui/hooks/useDebounce";
import { useCallback, useState } from "react";
import { MenuItemsPanelRow } from "./MenuItemsPanelRow";

const LoadingSkeleton = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={58} sx={{ borderRadius: "6px" }} />
    ))}
  </Box>
);

export const MenuItemsPanelContent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  const infiniteScrollResults = useInfiniteCursorConnectionScroll({
    useCursorConnectionQuery: useMenuItemSearchQuery,
    getQueryVariablesFromPagination: useCallback(
      ({ cursor, limit }) => ({
        variables: {
          query: debouncedSearchTerm,
          pagination: { cursor, limit },
        },
      }),
      [debouncedSearchTerm]
    ),
    queryKey: "menuItemCursorConnection",
    edgesAreEqual: useCallback((a: MenuItemPreviewSearchFragment, b: MenuItemPreviewSearchFragment) => a.id === b.id, []),
    limit: 25,
  });

  const { edges, loading, error, refetch, next } = infiniteScrollResults;
  const showInitialLoading = loading && edges === undefined && !error;
  const showInitialError = Boolean(error) && edges === undefined;
  const showFetchMoreError = Boolean(error) && edges !== undefined;
  const trimmedQuery = debouncedSearchTerm.trim();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0,
        px: 2,
        pb: 2,
      }}
    >
      <TextField
        fullWidth
        size="small"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        placeholder="Search menu item"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
          endAdornment:
            searchTerm !== "" ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearchTerm("")} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
        }}
      />

      <Divider />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          // Keep the scrollbar from reading as a continuous border beside rows
          scrollbarWidth: "thin",
          scrollbarColor: theme => `${theme.palette.greyscale[400]} transparent`,
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme => theme.palette.greyscale[400],
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        }}
      >
        {showInitialError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 2,
              py: 6,
              px: 1,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Couldn&apos;t load menu items. Try refreshing.
            </Typography>
            <Button variant="outlined" onClick={() => refetch()}>
              Retry
            </Button>
          </Box>
        ) : showInitialLoading ? (
          <LoadingSkeleton />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <InfiniteScrollContainer
              infiniteScrollResults={showFetchMoreError ? { ...infiniteScrollResults, hasNextPage: false } : infiniteScrollResults}
              loadingComponent={<Skeleton variant="rounded" height={58} sx={{ borderRadius: "6px", mt: 1 }} />}
              noItemsComponent={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 1,
                    py: 6,
                    px: 1,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {trimmedQuery ? `No items match '${trimmedQuery}'` : "There are no menu items yet."}
                  </Typography>
                </Box>
              }
              renderItem={menuItem => <MenuItemsPanelRow key={menuItem.id} menuItem={menuItem} />}
            />
            {showFetchMoreError && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1,
                  py: 2,
                  px: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Couldn&apos;t load more menu items. Try refreshing.
                </Typography>
                <Button variant="outlined" size="small" onClick={() => next()} disabled={loading}>
                  Retry
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
